import type { CompanyAccountingTerm, CompanyAccountingTermUpsertInput } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import useCompanyAccountingTerms from "@/screens/CompanySettings/Accounting/hooks/useCompanyAccountingTerms"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseAccountingTermsActionsProps {
  onSuccess?: () => void
}

const useCompanyAccountingTermsActions = ({
  onSuccess,
}: UseAccountingTermsActionsProps): {
  accountingTermsList: CompanyAccountingTerm[]
  addAccountingTermData: (inputs: CompanyAccountingTermUpsertInput[]) => Promise<void>
} => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { accountingTermsList, addAccountingTerm } = useCompanyAccountingTerms()

  const addAccountingTermData = async (inputs: CompanyAccountingTermUpsertInput[]) => {
    try {
      // Execute all mutations
      const results = await Promise.all(inputs.map((input) => addAccountingTerm({ input })))

      // Check if any mutations failed
      const errors = results.filter((result) => {
        return (
          result.error?.message ||
          result.data?.companyAccountingTerm.upsert.__typename === "CompanyAccountingTermFailure"
        )
      })

      if (errors.length > 0) {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: errors[0].error?.message ?? `Failed to set up accounting terms`,
        })
        return
      }

      onSuccess?.()
      notify({
        type: NotificationType.Success,
        silent: false,
        content: "Payment Terms and Credit Limits successfully set up",
      })
    } catch (error) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: `Failed to create accounting terms: ${error instanceof Error ? error.message : "Unknown error"}`,
      })
    }
  }

  return {
    addAccountingTermData,
    accountingTermsList,
  }
}

export default useCompanyAccountingTermsActions
